import { Component, inject, computed, signal, effect } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetsService } from '@lp-ui-lib';
import { UiService } from '@lp-ui-lib';
import { I18nService } from '@lp-ui-lib';
import { SettingsService } from '@lp-ui-lib';
import { StorageService } from '@lp-ui-lib';
import { environment } from '../../../../environments/environment';
import { AssetDetail } from '@lp-ui-lib';
import { NavButtonComponent } from '@lp-ui-lib';
import { SpinnerComponent } from '@lp-ui-lib';
import { SidebarComponent } from '@lp-ui-lib';
import { BreadcrumbsComponent } from '@lp-ui-lib';
import { SensorValueComponent } from '../../../components/sensor-value/sensor-value';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { GLOBAL_CONSTANT } from '@lp-ui-lib';
import { MEASUREMENT_TYPE } from '@lp-ui-lib';
import { App_Routes } from '@lp-ui-lib';

@Component({
  selector: 'app-sensor-health-page',
  imports: [
    NzIconModule,
    NavButtonComponent,
    SpinnerComponent,
    SidebarComponent,
    BreadcrumbsComponent,
    SensorValueComponent,
  ],
  templateUrl: './sensor-health-page.html',
  styleUrl: './sensor-health-page.scss',
})
export class SensorHealthPage {
  private assetsService = inject(AssetsService);
  private route = inject(ActivatedRoute);
  public router = inject(Router);
  private ui = inject(UiService);
  public i18n = inject(I18nService);
  private settings = inject(SettingsService);
  private storage = inject(StorageService);

  asset = signal<AssetDetail | null>(null);
  loading = signal<boolean>(false);
  plantID = signal<number>(GLOBAL_CONSTANT.NEGATIVE_ONE);
  edgeDeviceID = signal<string>(GLOBAL_CONSTANT.EMPTY_STRING);

  isMobile = computed(() => this.ui.IsMobile);
  isTablet = computed(() => this.ui.IsTablet);
  isDesktop = computed(() => this.ui.IsDesktop);

  HealthTelemetry = computed((): any => {
    const asset = this.asset();
    if (!asset || !asset.health) return [];
    return asset.health.filter(
      (telemetry: any) =>
        telemetry.measurementTypeID !== MEASUREMENT_TYPE.ID_1048 &&
        telemetry.measurementTypeID !== MEASUREMENT_TYPE.ID_1025 &&
        telemetry.measurementTypeID !== MEASUREMENT_TYPE.ID_1026
    );
  });

  DisplayErrorCard = computed(() => !!this.HealthTelemetry().find((item: any) => item.critical));

  InstallationAgentURL = computed(() => {
    const token = this.storage.token();
    const targetUrl = `${App_Routes.PLANT_EDGE_DEVICE_PATH}${this.plantID()}/${this.edgeDeviceID()}`;
    const encoded = encodeURIComponent(btoa(JSON.stringify({ targetUrl })));
    return `${environment.installation_agent_url}${App_Routes.LOGIN_REF_AUTH_PATH}${encodeURIComponent(token)}/${encoded}`;
  });

  Breadcrumbs = computed((): any[] => {
    const asset: any = this.asset();
    return asset ? asset.breadcrumbs.concat(asset) : [];
  });

  constructor() {
    effect(() => {
      this.ui.onNavBack(() => this.goBack());
    });

    effect(() => {
      const tabIndex = this.route.snapshot.data['tabIndex'];
      const backBtn = this.route.snapshot.data['backBtn'];
      this.ui.updateTabIndex(tabIndex);
      this.ui.updateBackBtn(backBtn);
    });
    this.refresh();
  }

  refresh(disableCache = false) {
    this.loading.set(true);
    const idParam = this.route.snapshot.paramMap.get('id');
    const assetID = idParam ? +idParam : GLOBAL_CONSTANT.NEGATIVE_ONE;

    this.settings.getUserSettings().then((settings: any) => {
      this.assetsService
        .getAssetEM(assetID, disableCache, settings.MeasurementSystem)
        .then((result1: any) => {
          this.assetsService
            .getSensorsEM(assetID, disableCache, settings.MeasurementSystem)
            .then((result2: any) => {
              this.assetsService.getNotifications().then((notifications: any) => {
                result1.asset.notifications = notifications?.filter(
                  (n: any) => n.objectID === result1?.asset?.objectID
                );

                result1.asset.sensors = result2?.sensors;
                result1.asset.health = result2?.health;
                result1.asset.steamTrap = result2?.steamtrap;
                result1.asset.temperature = result2?.temperature;

                this.asset.set(new AssetDetail(result1?.asset, result1?.breadcrumbs));
                this.plantID.set(result1?.plant ? result1?.plant?.objectID : -1);

                const temp = this.HealthTelemetry().find((item: any) => item.critical);
                const sensor =
                  temp?.sensor &&
                  this.asset()?.sensors?.find((item) => item.sensorID === temp.sensor.sensorID);

                this.edgeDeviceID.set(sensor?.edgeDigitID ?? '');
                this.ui.setBackText(this.i18n.parse(this.asset()?.objectName));
                this.loading.set(false);
              });
            });
        });
    });
  }

  toSensorValue(sensorID: number, measurementTypeID: number) {
    if (sensorID && measurementTypeID) {
      this.router.navigate([
        App_Routes.SENSOR_VALUE,
        this.asset()?.objectID,
        sensorID,
        measurementTypeID,
      ]);
    }
  }

  goBack() {
    this.router.navigate([App_Routes.ASSET, this.asset()?.objectID, GLOBAL_CONSTANT.NEGATIVE_ONE], {
      replaceUrl: true,
    });
  }
}
